import React, { useState } from "react";
import { Link } from "react-router-dom";
import "../Home/Homepage.css";
import "./CurrencyPairManagementPage.css";
import "./AddCurrencyPairPage.css";

const STATIC_CURRENCIES = ["BTC", "ETH", "USDT", "BNB", "SOL", "XRP", "ADA", "DOGE", "AVAX", "MATIC", "LINK", "DOT"];

const AVAILABILITY_OPTIONS = [
  { value: "GLOBAL", label: "GLOBAL (Binance Data)" },
  { value: "LOCAL", label: "LOCAL" },
];
const PRICE_SOURCE_OPTIONS = [
  { value: "CMC", label: "CMC (CoinMarketCap)" },
  { value: "LOCAL", label: "LOCAL (Manual)" },
];
const MARGIN_TYPE_OPTIONS = [
  { value: "CROSS", label: "Cross Margin" },
  { value: "ISOLATED", label: "Isolated Margin" },
];
const LEVERAGE_OPTIONS = [1, 2, 3, 5, 10, 20, 50, 75, 100, 125];

const FuturesPairPage = () => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    baseCurrency: "",
    quoteCurrency: "",
    availability: "GLOBAL",
    priceSource: "CMC",
    maxLeverage: 125,
    defaultLeverage: 10,
    marginType: "CROSS",
  });
  const [pairForm, setPairForm] = useState(null);
  const [submitError, setSubmitError] = useState("");
  const [successModal, setSuccessModal] = useState(null);

  const updateForm = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const updatePairForm = (key, value) => setPairForm((f) => (f ? { ...f, [key]: value } : null));

  const canProceedStep1 = form.baseCurrency && form.quoteCurrency && form.baseCurrency !== form.quoteCurrency;

  const handleContinueToStep2 = (e) => {
    e?.preventDefault?.();
    setSubmitError("");
    setPairForm({
      base_currency_id: form.baseCurrency,
      quote_currency_id: form.quoteCurrency,
      available: form.availability,
      price_source: form.availability === "GLOBAL" ? "BINANCE" : form.priceSource,
      buy_price: "",
      sell_price: "",
      maker_fee: 0.05,
      taker_fee: 0.05,
      min_order_qty: "",
      max_order_qty: "",
      step_size: "",
      tick_size: "",
      min_notional: "",
      base_asset_precision: 8,
      quote_asset_precision: 8,
      max_leverage: form.maxLeverage,
      default_leverage: form.defaultLeverage,
      margin_type: form.marginType,
      maintenance_margin: 0.5,
      funding_rate: 0.0001,
      liquidation_fee: 0.012,
    });
    setStep(2);
  };

  const handleBack = () => {
    if (step > 1) {
      setStep((s) => s - 1);
      if (step === 2) {
        setPairForm(null);
      }
      setSubmitError("");
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    if (!pairForm) return;
    setSubmitError("");
    setSuccessModal({
      pairName: `${form.baseCurrency}/${form.quoteCurrency}`,
      availability: form.availability === "GLOBAL" ? "GLOBAL (Binance)" : `LOCAL (${form.priceSource})`,
      leverage: `${pairForm.default_leverage}x (max ${pairForm.max_leverage}x)`,
      marginType: pairForm.margin_type,
    });
  };

  const handleSuccessClose = () => {
    setSuccessModal(null);
    setForm({
      baseCurrency: "",
      quoteCurrency: "",
      availability: "GLOBAL",
      priceSource: "CMC",
      maxLeverage: 125,
      defaultLeverage: 10,
      marginType: "CROSS",
    });
    setPairForm(null);
    setStep(1);
  };

  return (
    <main className="dashboard-main dashboard-subpage addpair-page">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Futures Trading</span>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Add Futures Pair</span>
          </p>
          <h1 className="dashboard-hero__title">Add Futures Pair</h1>
          <p className="dashboard-hero__subtitle">Create a new futures trading pair with leverage and margin settings</p>
        </div>
        <div className="dashboard-hero__icon addpair-hero__icon">
          <i className="fas fa-chart-line" />
        </div>
      </section>

      <div className="dashboard-body addpair-body">
        <div className="addpair-card">
          <div className="cp-stepper addpair-stepper">
            <div className={`cp-stepper-item ${step >= 1 ? "active" : ""}`}>
              <span className="cp-stepper-num">{step > 1 ? <i className="fas fa-check" /> : "1"}</span>
              <span className="cp-stepper-label">Select Currencies & Leverage</span>
            </div>
            <div className="cp-stepper-line" />
            <div className={`cp-stepper-item ${step >= 2 ? "active" : ""}`}>
              <span className="cp-stepper-num">{step > 2 ? <i className="fas fa-check" /> : "2"}</span>
              <span className="cp-stepper-label">Configure</span>
            </div>
            <div className="cp-stepper-line" />
            <div className={`cp-stepper-item ${step >= 3 ? "active" : ""}`}>
              <span className="cp-stepper-num">3</span>
              <span className="cp-stepper-label">Confirm</span>
            </div>
          </div>

          {submitError && (
            <div className="addpair-alert addpair-alert--error">
              <i className="fas fa-exclamation-circle" />
              <span>{submitError}</span>
            </div>
          )}

          {step === 1 && (
            <form onSubmit={handleContinueToStep2} className="addpair-form">
              <div className="addpair-config-grid">
                <div className="cp-field">
                  <label>Base Currency <span className="required">*</span></label>
                  <div className="cp-input-wrap">
                    <i className="fas fa-gem" />
                    <select
                      value={form.baseCurrency}
                      onChange={(e) => updateForm("baseCurrency", e.target.value)}
                      required
                    >
                      <option value="">Select Base Currency</option>
                      {STATIC_CURRENCIES.filter((c) => c !== form.quoteCurrency).map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="cp-field">
                  <label>Quote Currency <span className="required">*</span></label>
                  <div className="cp-input-wrap">
                    <i className="fas fa-gem" />
                    <select
                      value={form.quoteCurrency}
                      onChange={(e) => updateForm("quoteCurrency", e.target.value)}
                      required
                    >
                      <option value="">Select Quote Currency</option>
                      {STATIC_CURRENCIES.filter((c) => c !== form.baseCurrency).map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="cp-field">
                  <label>Availability <span className="required">*</span></label>
                  <div className="cp-input-wrap">
                    <i className="fas fa-globe" />
                    <select
                      value={form.availability}
                      onChange={(e) => updateForm("availability", e.target.value)}
                    >
                      {AVAILABILITY_OPTIONS.map((a) => (
                        <option key={a.value} value={a.value}>{a.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {form.availability === "LOCAL" && (
                  <div className="cp-field">
                    <label>Price Source <span className="required">*</span></label>
                    <div className="cp-input-wrap">
                      <i className="fas fa-chart-line" />
                      <select
                        value={form.priceSource}
                        onChange={(e) => updateForm("priceSource", e.target.value)}
                      >
                        {PRICE_SOURCE_OPTIONS.map((p) => (
                          <option key={p.value} value={p.value}>{p.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
                <div className="cp-field">
                  <label>Max Leverage <span className="required">*</span></label>
                  <div className="cp-input-wrap">
                    <i className="fas fa-layer-group" />
                    <select
                      value={form.maxLeverage}
                      onChange={(e) => updateForm("maxLeverage", Number(e.target.value))}
                    >
                      {LEVERAGE_OPTIONS.map((lev) => (
                        <option key={lev} value={lev}>{lev}x</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="cp-field">
                  <label>Default Leverage <span className="required">*</span></label>
                  <div className="cp-input-wrap">
                    <i className="fas fa-layer-group" />
                    <select
                      value={form.defaultLeverage}
                      onChange={(e) => updateForm("defaultLeverage", Number(e.target.value))}
                    >
                      {LEVERAGE_OPTIONS.filter((l) => l <= form.maxLeverage).map((lev) => (
                        <option key={lev} value={lev}>{lev}x</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="cp-field">
                  <label>Margin Type <span className="required">*</span></label>
                  <div className="cp-input-wrap">
                    <i className="fas fa-shield-alt" />
                    <select
                      value={form.marginType}
                      onChange={(e) => updateForm("marginType", e.target.value)}
                    >
                      {MARGIN_TYPE_OPTIONS.map((m) => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="addpair-actions">
                <Link to="/dashboard/homepage" className="cp-btn cp-btn--outline">Cancel</Link>
                <button type="submit" className="cp-btn cp-btn--primary" disabled={!canProceedStep1}>
                  Continue <i className="fas fa-arrow-right" />
                </button>
              </div>
            </form>
          )}

          {step === 2 && pairForm && (
            <form onSubmit={(e) => { e.preventDefault(); setStep(3); }} className="addpair-form">
              <p className="cp-step-desc">Edit values as needed. (Static demo - no API)</p>
              <div className="addpair-config-grid">
                <div className="cp-field">
                  <label>Buy Price</label>
                  <div className="cp-input-wrap">
                    <input type="number" step="any" min={0} value={pairForm.buy_price} onChange={(e) => updatePairForm("buy_price", e.target.value)} onWheel={(e) => e.target.blur()} required />
                  </div>
                </div>
                <div className="cp-field">
                  <label>Sell Price</label>
                  <div className="cp-input-wrap">
                    <input type="number" step="any" min={0} value={pairForm.sell_price} onChange={(e) => updatePairForm("sell_price", e.target.value)} onWheel={(e) => e.target.blur()} required />
                  </div>
                </div>
                <div className="cp-field">
                  <label>Maker Fee (%)</label>
                  <div className="cp-input-wrap">
                    <input type="number" step="0.01" min={0} value={pairForm.maker_fee} onChange={(e) => updatePairForm("maker_fee", e.target.value)} onWheel={(e) => e.target.blur()} />
                  </div>
                </div>
                <div className="cp-field">
                  <label>Taker Fee (%)</label>
                  <div className="cp-input-wrap">
                    <input type="number" step="0.01" min={0} value={pairForm.taker_fee} onChange={(e) => updatePairForm("taker_fee", e.target.value)} onWheel={(e) => e.target.blur()} />
                  </div>
                </div>
                <div className="cp-field">
                  <label>Min Order Qty</label>
                  <div className="cp-input-wrap">
                    <input type="number" step="any" min={0} value={pairForm.min_order_qty} onChange={(e) => updatePairForm("min_order_qty", e.target.value)} onWheel={(e) => e.target.blur()} required />
                  </div>
                </div>
                <div className="cp-field">
                  <label>Max Order Qty</label>
                  <div className="cp-input-wrap">
                    <input type="number" step="any" min={0} value={pairForm.max_order_qty} onChange={(e) => updatePairForm("max_order_qty", e.target.value)} onWheel={(e) => e.target.blur()} required />
                  </div>
                </div>
                <div className="cp-field">
                  <label>Step Size</label>
                  <div className="cp-input-wrap">
                    <input type="number" step="any" min={0} value={pairForm.step_size} onChange={(e) => updatePairForm("step_size", e.target.value)} onWheel={(e) => e.target.blur()} required />
                  </div>
                </div>
                <div className="cp-field">
                  <label>Tick Size</label>
                  <div className="cp-input-wrap">
                    <input type="number" step="any" min={0} value={pairForm.tick_size} onChange={(e) => updatePairForm("tick_size", e.target.value)} onWheel={(e) => e.target.blur()} required />
                  </div>
                </div>
                <div className="cp-field">
                  <label>Min Notional</label>
                  <div className="cp-input-wrap">
                    <input type="number" step="any" min={0} value={pairForm.min_notional} onChange={(e) => updatePairForm("min_notional", e.target.value)} onWheel={(e) => e.target.blur()} required />
                  </div>
                </div>
                <div className="cp-field">
                  <label>Maintenance Margin (%)</label>
                  <div className="cp-input-wrap">
                    <input type="number" step="0.01" min={0} value={pairForm.maintenance_margin} onChange={(e) => updatePairForm("maintenance_margin", e.target.value)} onWheel={(e) => e.target.blur()} />
                  </div>
                </div>
                <div className="cp-field">
                  <label>Funding Rate</label>
                  <div className="cp-input-wrap">
                    <input type="number" step="0.0001" value={pairForm.funding_rate} onChange={(e) => updatePairForm("funding_rate", e.target.value)} onWheel={(e) => e.target.blur()} />
                  </div>
                </div>
                <div className="cp-field">
                  <label>Liquidation Fee (%)</label>
                  <div className="cp-input-wrap">
                    <input type="number" step="0.001" min={0} value={pairForm.liquidation_fee} onChange={(e) => updatePairForm("liquidation_fee", e.target.value)} onWheel={(e) => e.target.blur()} />
                  </div>
                </div>
                <div className="cp-field">
                  <label>Base Asset Precision</label>
                  <div className="cp-input-wrap">
                    <input type="number" min={0} max={18} value={pairForm.base_asset_precision} onChange={(e) => updatePairForm("base_asset_precision", e.target.value)} onWheel={(e) => e.target.blur()} />
                  </div>
                </div>
                <div className="cp-field">
                  <label>Quote Asset Precision</label>
                  <div className="cp-input-wrap">
                    <input type="number" min={0} max={18} value={pairForm.quote_asset_precision} onChange={(e) => updatePairForm("quote_asset_precision", e.target.value)} onWheel={(e) => e.target.blur()} />
                  </div>
                </div>
              </div>
              <div className="addpair-actions">
                <button type="button" className="cp-btn cp-btn--outline" onClick={handleBack}>
                  <i className="fas fa-arrow-left" /> Back
                </button>
                <button type="submit" className="cp-btn cp-btn--primary">
                  Continue <i className="fas fa-arrow-right" />
                </button>
              </div>
            </form>
          )}

          {step === 3 && pairForm && (
            <div className="addpair-form">
              <p className="cp-step-desc">Confirm and create the futures trading pair. (Static demo)</p>
              <div className="addpair-summary">
                <div className="addpair-summary-row"><span>Pair</span><strong>{form.baseCurrency}/{form.quoteCurrency}</strong></div>
                <div className="addpair-summary-row"><span>Availability</span><strong>{form.availability}</strong></div>
                <div className="addpair-summary-row"><span>Leverage</span><strong>{pairForm.default_leverage}x (max {pairForm.max_leverage}x)</strong></div>
                <div className="addpair-summary-row"><span>Margin Type</span><strong>{pairForm.margin_type}</strong></div>
                <div className="addpair-summary-row"><span>Buy / Sell Price</span><strong>{pairForm.buy_price || "—"} / {pairForm.sell_price || "—"}</strong></div>
                <div className="addpair-summary-row"><span>Maker / Taker Fee</span><strong>{pairForm.maker_fee}% / {pairForm.taker_fee}%</strong></div>
                <div className="addpair-summary-row"><span>Maintenance Margin</span><strong>{pairForm.maintenance_margin}%</strong></div>
              </div>
              <div className="addpair-actions">
                <button type="button" className="cp-btn cp-btn--outline" onClick={handleBack}>
                  <i className="fas fa-arrow-left" /> Back
                </button>
                <button type="button" className="cp-btn cp-btn--primary" onClick={handleSubmit}>
                  <i className="fas fa-check" /> Create Futures Pair
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {successModal && (
        <div className="addpair-success-overlay" onClick={handleSuccessClose}>
          <div className="addpair-success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="addpair-success-icon"><i className="fas fa-check-circle" /></div>
            <h3>Futures Pair Created Successfully</h3>
            <p>The following futures pair has been created (static demo):</p>
            <div className="addpair-success-details">
              <div className="addpair-success-row"><span>Pair</span><strong>{successModal.pairName}</strong></div>
              <div className="addpair-success-row"><span>Availability</span><strong>{successModal.availability}</strong></div>
              <div className="addpair-success-row"><span>Leverage</span><strong>{successModal.leverage}</strong></div>
              <div className="addpair-success-row"><span>Margin Type</span><strong>{successModal.marginType}</strong></div>
            </div>
            <button type="button" className="cp-btn cp-btn--primary" onClick={handleSuccessClose}>Done</button>
          </div>
        </div>
      )}
    </main>
  );
};

export default FuturesPairPage;
